import { get } from "lodash";
import { useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";

import { Button, cn } from "@shared/ui";
import Callout from "../components/Callout";
import { Field, inputClassName } from "../components/Field";
import ResendCodeButton from "../components/ResendCodeButton";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import CarrierMark from "../components/CarrierMark";
import { enterMfa, isAwaiting, isStarted, Operation, processDirectLink } from "../lib/axle";
import { MAX_HITL_STARTED_RETRIES } from "../lib/constants";
import {
  getIntegrationConfig,
  isIntegrationV123,
  useAccountHandler,
} from "../lib/utility";
import CarrierLoader from "./CarrierLoader";

// Cooldown (seconds) before a resent code can be requested again.
const RESEND_COOLDOWN_SECONDS = 30;

const showMfaFaq = (session, loginInformation, integration) => {
  const monitoringEnabled = get(session, "config.scopes.monitoring", false);
  const integrationConfig = getIntegrationConfig(
    session,
    loginInformation,
    integration
  );
  const mfaFaqUrl = get(integrationConfig, "mfaFaqUrl", false);
  // For more info refer to ign-backend/getSession on how this value is constructed
  let showMfaFaqUrl = mfaFaqUrl ? true : false;

  return monitoringEnabled && showMfaFaqUrl;
};

const EnterMfa = ({
  step,
  nextStep,
  setAccountInfo,
  loginInformation,
  session,
  setLinkError,
  posthog,
  setShowNav,
  client,
  integration,
}) => {
  // Track pageview
  useEffect(() => {
    posthog.capture("$pageview", {
      step,
      carrier: get(loginInformation, "carrier.id"),
    });
  }, [posthog]);

  let retryGetAccountAttempts = 0;
  const carrier = get(loginInformation, "carrier.id");
  const ignitionToken = session.id;
  // Absent for auto-send carriers, where SendMfa skips the chooser and no
  // delivery method is ever picked. Only the legacy enterMfa call reads it.
  const type = get(loginInformation, "mfa.type");

  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState(null);
  // MFA re-wait state: the prior code was rejected/expired and the run re-armed a
  // code-wait. awaitingAttempt (echoed from the awaiting response) makes the next
  // submit/resend attempt-distinct; mfaRejected drives the resend UI.
  const [awaitingAttempt, setAwaitingAttempt] = useState(null);
  const [mfaRejected, setMfaRejected] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // Resend cooldown countdown.
  useEffect(() => {
    if (timeLeft <= 0) return undefined;
    const id = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timeLeft]);

  // Configure carrier loader
  const { promiseInProgress } = usePromiseTracker({ area: "carrier-loader" });
  const loadingSteps = [
    "Establishing a secure connection",
    "Contacting your carrier",
    "Sending verification code",
    "Retrieving account information",
  ];

  // Import custom hook for getAccount()
  const getAccount = useAccountHandler({
    ignitionToken,
    integration,
    setAccountInfo,
    loginInformation,
    nextStep,
    setLinkError,
    setShowNav,
    retryGetAccountAttempts,
  });

  const toShowMfaFaq = showMfaFaq(session, loginInformation, integration);
  // Form logic
  const enterMfaGetAccount = async (startedPollAttempts = 0) => {
    // Hide nav buttons while CarrierLoader is rendered for accessibility
    setShowNav(false);

    if (verificationCode.length < 3) {
      // Backend will throw error if it recieves an MFA code that is too short.
      setError("Verification code must be 3+ characters.");
      return;
    } else {

      let [enterMfaResponse, enterMfaError] = [null, null]

      if (isIntegrationV123(loginInformation, integration, session)) {
        // On a re-wait echo the attempt so the corrected code is not deduped
        // against the rejected one; the first submit omits it.
        [enterMfaResponse, enterMfaError] = await processDirectLink(
          ignitionToken,
          integration,
          Operation.ENTER_MFA,
          awaitingAttempt !== null
            ? { code: verificationCode, attempt: awaitingAttempt }
            : { code: verificationCode }
        )
      }else{
      // Submit MFA code
      [enterMfaResponse, enterMfaError] = await enterMfa(
        ignitionToken,
        carrier,
        integration,
        type,
        verificationCode
      );
      }


      if (enterMfaError) {
        switch (get(enterMfaError, "code")) {
          // Incorrect mfa code
          case 400:
            setError(enterMfaError.message);
            return;
          // If session expired, send customer to session-expired
          case 401:
          case 403:
            setLinkError(enterMfaError.message);
            nextStep("session-expired");
            return;
          case 500:
            setLinkError("enter MFA code");
            nextStep("carrier-error");
          default:
            nextStep("connection-error");
            return;
        }
      }

      // The code was rejected/expired and the run re-armed a code-wait.
      // Keep the form, surface the error, and offer resend — do NOT getAccount.
      if (isAwaiting(enterMfaResponse)) {
        setMfaRejected(Boolean(get(enterMfaResponse, "mfaRejected")));
        setAwaitingAttempt(
          get(enterMfaResponse, "attempt", awaitingAttempt ?? 0)
        );
        setError(
          "That code was incorrect or expired. Re-enter it or request a new code."
        );
        return;
      }

      // Still verifying — re-poll enterMfa (backend skips re-dispatch when the
      // code was already accepted). Do not advance without an account.
      if (isStarted(enterMfaResponse)) {
        if (startedPollAttempts < MAX_HITL_STARTED_RETRIES) {
          return enterMfaGetAccount(startedPollAttempts + 1);
        }
        setLinkError("enter MFA code");
        nextStep("carrier-error");
        return;
      }
    }

    await getAccount();
  };

  // Resend a fresh code into the suspended code-wait (same run). The
  // orchestrator re-runs sendMfa and re-arms the wait, so the poll typically
  // resolves awaiting again — we just refresh the attempt and start the cooldown.
  const onResend = async () => {
    setIsResending(true);
    const [resendResponse, resendError] = await processDirectLink(
      ignitionToken,
      integration,
      Operation.RESEND_MFA,
      { attempt: awaitingAttempt ?? 0 }
    );
    setIsResending(false);
    if (resendError) {
      setError("We couldn't resend the code. Please try again.");
      return;
    }
    if (isAwaiting(resendResponse)) {
      setAwaitingAttempt(get(resendResponse, "attempt", awaitingAttempt ?? 0));
    }
    setError(null);
    setVerificationCode("");
    setTimeLeft(RESEND_COOLDOWN_SECONDS);
  };

  const onSubmit = async (e) => {
    // Prevent default form behavior
    e.preventDefault();

    // Validate required input fields
    if (!verificationCode) {
      setError("Enter your verification code");
      return;
    }

    // Enter MFA code and get account details
    await trackPromise(enterMfaGetAccount(), "carrier-loader");

    // Show nav buttons after CarrierLoader is unrendered
    setShowNav(true);
  };

  return (
    <>
      {promiseInProgress ? (
        <CarrierLoader
          loginInformation={loginInformation}
          loadingHeader="Verifying identity"
          loadingSteps={loadingSteps}
        />
      ) : (
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit}>
          <StepBody>
            <CarrierMark loginInformation={loginInformation} />
            <StepHeading
              title="Verify your identity"
              description={
                <>
                  Enter the code you received from{" "}
                  <span className="font-medium text-ink">
                    {loginInformation.carrier.name}
                  </span>
                  .
                </>
              }
            />
            {toShowMfaFaq && (
              <Callout
                type="info"
                message={
                  <p className="text-body-lg">
                    To avoid re-verifying your insurance information with{" "}
                    {get(client, "displayName", "provider")}, consider disabling
                    multi-factor authentication on your{" "}
                    {get(loginInformation, "carrier.name")} account. Visit{" "}
                    {get(
                      loginInformation,
                      "carrier.name",
                      "insurance provider"
                    )}
                    's{" "}
                    <a
                      target="_blank"
                      href={get(
                        loginInformation,
                        "carrier.mfaFaqUrl",
                        undefined
                      )}
                      className={
                        get(loginInformation, "carrier.mfaFaqUrl", undefined) &&
                        "font-medium underline hover:text-ink focus:text-ink"
                      }
                    >
                      website
                    </a>{" "}
                    for more information.
                  </p>
                }
              />
            )}
            <div className="flex flex-col gap-3">
              <Field id="mfa-code" label="Verification code" error={error}>
                <input
                  id="mfa-code"
                  aria-label="Verification code"
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "mfa-code-error" : undefined}
                  value={verificationCode}
                  className={cn(inputClassName, "font-mono")}
                  type="text"
                  onChange={(e) => setVerificationCode(e.target.value)}
                />
              </Field>
              {mfaRejected && (
                <ResendCodeButton
                  timeLeft={timeLeft}
                  isResending={isResending}
                  onResend={onResend}
                  posthog={posthog}
                />
              )}
            </div>
          </StepBody>
          <StepFooter>
            <Button
              //onClick={() => nextStep(step)}
              type={"submit"}
            >
              Verify
            </Button>
          </StepFooter>
        </form>
      )}
    </>
  );
};

export default EnterMfa;
